import { formatOeDisplay } from '@/utils/partDisplay';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, ArrowRight, Package2, Users, Truck, ChevronLeft, ChevronRight, ChevronDown, Loader2, X, Package, ShoppingCart, Share2, FileText, Home, ShieldCheck, Wrench } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useCustomerView } from '@/hooks/useCustomerView';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import PartCard from '@/components/PartCard';
import CreateCustomerOrder from '@/components/CreateCustomerOrder';
import CreateQuotationModal from '@/components/CreateQuotationModal';
import CatalogueOrderModal from '@/components/CatalogueOrderModal';
import SearchSuggestions from '@/components/SearchSuggestions';
import QuotationNotifications from '@/components/QuotationNotifications';
import NtoReceivedNotification from '@/components/NtoReceivedNotification';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Admin mobile numbers
const MAIN_ADMIN_MOBILE = "9999313175";
const SUB_ADMIN_MOBILES = ["9811117751", "9643558419"];

// Custom hook for debounced value with longer delay to prevent stale results
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const HomePage = () => {
  const navigate = useNavigate();
  const { brand } = useParams(); // Get brand from URL
  const { token, user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [banners, setBanners] = useState([]);
  const [currentBanner, setCurrentBanner] = useState(0);
  
  // Master category state - use brand from URL or localStorage
  const [masterCategories, setMasterCategories] = useState([]);
  const [selectedMasterCategory, setSelectedMasterCategory] = useState(brand || localStorage.getItem('selectedCategory') || '');
  
  // Update selectedMasterCategory when brand param changes
  useEffect(() => {
    if (brand) {
      setSelectedMasterCategory(brand);
      localStorage.setItem('selectedCategory', brand);
    }
  }, [brand]);
  
  // Refs for auto-scroll to search results and search input
  const jpSearchResultsRef = useRef(null);
  const oeSearchResultsRef = useRef(null);
  const jpSearchInputRef = useRef(null);
  const oeSearchInputRef = useRef(null);
  
  // CRITICAL: AbortController refs to cancel stale requests
  const jpSearchAbortRef = useRef(null);
  const oeSearchAbortRef = useRef(null);
  
  // Track current search query to ensure results match
  const currentJpQueryRef = useRef('');
  const currentOeQueryRef = useRef('');
  
  // REQUEST ID COUNTERS - Key to preventing stale results
  // Increment on each search, only accept responses matching current ID
  const jpRequestIdRef = useRef(0);
  const oeRequestIdRef = useRef(0);
  
  // Search suggestions state
  const [showJpSuggestions, setShowJpSuggestions] = useState(false);
  const [showOeSuggestions, setShowOeSuggestions] = useState(false);
  // Brands switcher dropdown (top-left of hero — "All Brands ▾")
  const [brandsMenuOpen, setBrandsMenuOpen] = useState(false);
  const [currentSearchQuery, setCurrentSearchQuery] = useState(''); // Track query for zero-click detection
  
  // Pagination state for infinite scroll
  const [displayedResults, setDisplayedResults] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const RESULTS_PER_PAGE = 12;
  
  // OE MRP Search state
  const [oeSearchQuery, setOeSearchQuery] = useState('');
  const [oeSearchResults, setOeSearchResults] = useState([]);
  const [isOeSearching, setIsOeSearching] = useState(false);
  const [oeDiscount, setOeDiscount] = useState(0);
  const [sharingOe, setSharingOe] = useState(null); // Track which OE part is being shared
  
  // Create Customer Order state (for admins)
  const [showCreateOrder, setShowCreateOrder] = useState(false);
  const [showCreateQuotation, setShowCreateQuotation] = useState(false);
  const [showCatalogueOrder, setShowCatalogueOrder] = useState(false);
  
  // Check if user is admin (masked by Customer View toggle so admins can
  // preview the catalogue exactly like a regular customer would see it).
  const { isCustomerView } = useCustomerView();
  const rawIsAdmin = user?.mobile === MAIN_ADMIN_MOBILE || SUB_ADMIN_MOBILES.includes(user?.mobile);
  const rawIsStaffUser = user?.is_staff_user || false;
  const isAdmin = rawIsAdmin && !isCustomerView;
  const isStaffUser = rawIsStaffUser && !isCustomerView;
  const canCreateCustomerOrder = isAdmin || isStaffUser;

  // Debounced search query for live search (500ms delay - longer to prevent stale results)
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const debouncedOeSearchQuery = useDebounce(oeSearchQuery, 500);

  // Load master categories from prefetch cache
  useEffect(() => {
    const loadMasterCategories = async () => {
      try {
        const { getCachedMasterCategories } = await import('@/utils/prefetchCache');
        const cached = await getCachedMasterCategories();
        if (cached && cached.length > 0) {
          setMasterCategories(cached);
        } else {
          const response = await axios.get(`${API}/master-categories/active`);
          if (response.data.success && response.data.categories) {
            setMasterCategories(response.data.categories);
          }
        }
      } catch (error) {
        console.log('Failed to load master categories');
      }
    };
    loadMasterCategories();
  }, []);

  // Handle master category change
  const handleMasterCategoryChange = (categoryName) => {
    setSelectedMasterCategory(categoryName);
    localStorage.setItem('selectedCategory', categoryName);
    // Clear search results when changing category
    setSearchResults([]);
    setDisplayedResults([]);
    setSearchQuery('');
  };

  useEffect(() => {
    const loadStartupData = async () => {
      // Load banners and OE discount from prefetch cache
      try {
        const { getCachedBanners, getCachedOeDiscount } = await import('@/utils/prefetchCache');
        const [cachedBanners, cachedDiscount] = await Promise.all([
          getCachedBanners(),
          token ? getCachedOeDiscount() : Promise.resolve(null),
        ]);
        if (cachedBanners && Array.isArray(cachedBanners)) setBanners(cachedBanners);
        if (cachedDiscount !== null) setOeDiscount(cachedDiscount);
      } catch {
        // Fallback to direct API
        try {
          const response = await axios.get(`${API}/banners`);
          setBanners(response.data);
        } catch { /* no banners */ }
        if (token) {
          axios.get(`${API}/customer/oe-discount`, { headers: { Authorization: `Bearer ${token}` } })
            .then(res => setOeDiscount(res.data.oe_discount_percentage || 0))
            .catch(() => {});
        }
      }
    };
    loadStartupData();
  }, [token]);

  // Auto-slide banners
  useEffect(() => {
    if (banners.length > 1) {
      const interval = setInterval(() => {
        setCurrentBanner((prev) => (prev + 1) % banners.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [banners.length]);

  // Log search query for personalization
  const logSearch = async (query) => {
    if (!token || !query.trim()) return;
    try {
      await axios.post(`${API}/search/log`, { query }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentSearchQuery(query); // Track for zero-click detection
    } catch (e) { /* silent */ }
  };

  // Log when user clicks a part from search results
  const logSearchClick = async (part, query) => {
    if (!token || !part?.jp_part_no) return;
    try {
      await axios.post(`${API}/search/click`, {
        jp_part_no: part.jp_part_no,
        query: query
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (e) { /* silent */ }
  };

  // Log zero-click search (user searched but didn't click anything)
  const logZeroClickSearch = async (query, resultsCount) => {
    if (!token || !query.trim()) return;
    try {
      await axios.post(`${API}/search/log-zero-click`, {
        query,
        results_count: resultsCount
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (e) { /* silent */ }
  };

  // Track if user clears search without clicking (zero-click)
  useEffect(() => {
    // If search was cleared and there were results but no clicks
    if (!searchQuery.trim() && currentSearchQuery && displayedResults.length > 0) {
      logZeroClickSearch(currentSearchQuery, displayedResults.length);
      setCurrentSearchQuery('');
    }
  }, [searchQuery]);

  // Live search effect - CLEARS results before new search to prevent ghost results
  useEffect(() => {
    if (debouncedSearchQuery.trim() && token) {
      // CRITICAL: Clear previous results BEFORE starting new search
      setSearchResults([]);
      setDisplayedResults([]);
      setTotalResults(0);
      setHasMore(false);
      logSearch(debouncedSearchQuery.trim()); // Log the search
      searchParts(debouncedSearchQuery.trim());
    } else if (!debouncedSearchQuery.trim()) {
      setSearchResults([]);
      setDisplayedResults([]);
      setTotalResults(0);
      setHasMore(false);
    }
  }, [debouncedSearchQuery, token]);

  // Auto-scroll to JP search results when they appear
  useEffect(() => {
    if (displayedResults.length > 0 && jpSearchResultsRef.current) {
      setTimeout(() => {
        jpSearchResultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [displayedResults.length > 0]); // Only trigger when results first appear

  // OE MRP Live search effect
  useEffect(() => {
    if (debouncedOeSearchQuery.trim() && token) {
      searchOeParts(debouncedOeSearchQuery.trim());
    } else if (!debouncedOeSearchQuery.trim()) {
      setOeSearchResults([]);
    }
  }, [debouncedOeSearchQuery, token]);

  // Auto-scroll to OE search results when they appear
  useEffect(() => {
    if (oeSearchResults.length > 0 && oeSearchResultsRef.current) {
      setTimeout(() => {
        oeSearchResultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [oeSearchResults.length > 0]); // Only trigger when results first appear

  // Search OE parts with request ID tracking to prevent stale results
  const searchOeParts = async (query) => {
    if (!token) return;
    
    // CRITICAL: Cancel any previous OE search request
    if (oeSearchAbortRef.current) {
      oeSearchAbortRef.current.abort();
    }
    
    // Create new abort controller for this request
    const abortController = new AbortController();
    oeSearchAbortRef.current = abortController;
    
    // Store the current query and increment request ID
    const thisQuery = query.trim();
    const thisRequestId = ++oeRequestIdRef.current;
    currentOeQueryRef.current = thisQuery;
    
    setIsOeSearching(true);
    try {
      // Brand-aware OE MRP lookup (2026-06-06). The HomePage drives the
      // OEM-brand context via `selectedMasterCategory`; we pass it down
      // so the backend filters the OE catalog to that brand only (Mazda
      // numbers must never bleed into Eicher results and vice versa).
      const brandParam = selectedMasterCategory === 'Swaraj Mazda' || selectedMasterCategory === 'Eicher'
        ? `&brand=${encodeURIComponent(selectedMasterCategory)}`
        : '';
      const response = await axios.get(
        `${API}/oe-mrp/search?q=${encodeURIComponent(thisQuery)}&limit=20${brandParam}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          signal: abortController.signal
        }
      );
      
      // CRITICAL: Triple check - request ID, query match, and abort state
      // This bulletproofs against race conditions
      if (thisRequestId !== oeRequestIdRef.current) {
        console.log(`[OE Search] Discarding stale results (request #${thisRequestId}, current #${oeRequestIdRef.current})`);
        return;
      }
      if (currentOeQueryRef.current !== thisQuery) {
        console.log(`[OE Search] Query changed, discarding results for "${thisQuery}"`);
        return;
      }
      
      setOeSearchResults(response.data.results || []);
      setOeDiscount(response.data.oe_discount_percentage || 0);
    } catch (error) {
      // Don't treat abort as an error
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        console.log(`[OE Search] Request aborted for "${thisQuery}"`);
        return;
      }
      // Only update error state if this is still current request
      if (thisRequestId === oeRequestIdRef.current) {
        console.error('Error searching OE parts:', error);
        setOeSearchResults([]);
      }
    } finally {
      // Only clear loading if this is still the current request
      if (thisRequestId === oeRequestIdRef.current) {
        setIsOeSearching(false);
      }
    }
  };

  const clearOeSearch = () => {
    setOeSearchQuery('');
    setOeSearchResults([]);
  };

  // Share OE part via WhatsApp (Admin only) - with image card
  // jpPart is optional - if provided and has image, use it for OE card
  const shareOePart = async (oePart, jpPart = null) => {
    if (!isAdmin || !oePart || sharingOe) return; // Prevent double-clicks
    
    setSharingOe(oePart.oe_part_no);
    let shareCard = null;
    
    try {
      // Try to fetch JP part image if JP part is provided and has an image
      let productImageUrl = null;
      if (jpPart?.image) {
        try {
          const imgResponse = await axios.get(`${API}/image/${encodeURIComponent(jpPart.image)}`);
          if (imgResponse.data.data_url) {
            productImageUrl = imgResponse.data.data_url;
          }
        } catch (e) {
          console.log('Could not fetch JP image:', e);
        }
      }

      // Create a temporary card element for html2canvas
      shareCard = document.createElement('div');
      shareCard.style.cssText = 'position: fixed; left: -9999px; top: 0; width: 320px; background: white; border-radius: 12px; overflow: hidden; font-family: system-ui, -apple-system, sans-serif; box-shadow: 0 4px 6px rgba(0,0,0,0.1);';

      const logoUrl = window.location.origin + '/jp_logo.png';
      
      // No image placeholder as data URL (simple package icon)
      const noImageSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><path d="M16.5 9.4l-9-5.19M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>`;
      const noImageDataUrl = `data:image/svg+xml,${encodeURIComponent(noImageSvg)}`;
      
      // Use JP image if available, otherwise placeholder
      const displayImageUrl = productImageUrl || noImageDataUrl;
      const hasProductImage = !!productImageUrl;
      
      // Calculate discount info
      const hasDiscount = oeDiscount > 0 && oePart.discounted_price;
      const discountedPrice = oePart.discounted_price || oePart.mrp;

      // MRP change calculation for share card
      const oldMrp = oePart.old_mrp;
      const newMrp = oePart.mrp;
      const effDate = oePart.mrp_effective_date;
      let mrpChangeHtml = '';
      let mrpChangeText = '';
      if (oldMrp && effDate && oldMrp !== newMrp) {
        const effMs = new Date(effDate).getTime();
        const daysSince = (Date.now() - effMs) / (1000 * 60 * 60 * 24);
        if (daysSince <= 60) {
          const change = newMrp - oldMrp;
          const pct = ((change / oldMrp) * 100).toFixed(1);
          const isUp = change > 0;
          mrpChangeHtml = `
            <div style="background: ${isUp ? '#fef2f2' : '#f0fdf4'}; border: 1px solid ${isUp ? '#fecaca' : '#bbf7d0'}; border-radius: 8px; padding: 10px; margin-top: 8px;">
              <div style="font-size: 12px; font-weight: bold; color: ${isUp ? '#dc2626' : '#16a34a'}; margin-bottom: 4px;">
                MRP ${isUp ? 'INCREASED' : 'DECREASED'} (Effective ${new Date(effDate).toLocaleDateString('en-IN', {day: 'numeric', month: 'short', year: 'numeric'})})
              </div>
              <div style="display: flex; justify-content: space-between; font-size: 13px;">
                <span style="color: #6b7280;">Previous: <span style="text-decoration: line-through;">₹${oldMrp?.toLocaleString()}</span></span>
                <span style="color: ${isUp ? '#dc2626' : '#16a34a'}; font-weight: bold;">${isUp ? '↑' : '↓'} ${isUp ? '+' : ''}${pct}%</span>
              </div>
            </div>
          `;
          mrpChangeText = `\nMRP ${isUp ? 'Increased' : 'Decreased'}: ₹${oldMrp?.toLocaleString()} → ₹${newMrp?.toLocaleString()} (${isUp ? '+' : ''}${pct}%)`;
        }
      }

      // Match exact style of original JP part card
      shareCard.innerHTML = `
        <div style="background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); height: 220px; display: flex; align-items: center; justify-content: center; position: relative; padding: 0; margin: 0;">
          <div style="position: absolute; top: 12px; left: 12px; color: #f59e0b; font-size: 18px; font-weight: bold;">EICHER OE</div>
          ${hasProductImage 
            ? `<img src="${displayImageUrl}" style="width: 100%; height: 100%; object-fit: contain;" />`
            : `<img src="${noImageDataUrl}" style="width: 100px; height: 100px; object-fit: contain;" />`
          }
        </div>
        <div style="padding: 16px; background: white;">
          <h3 style="font-size: 16px; font-weight: bold; color: #1f2937; margin: 0 0 16px 0; line-height: 1.3;">
            ${oePart.part_name}
          </h3>
          <div style="margin-bottom: 8px;">
            <span style="color: #666; font-size: 14px;">OE Part No: </span>
            <span style="color: #b45309; font-weight: bold; font-size: 14px;">${formatOeDisplay(oePart.oe_part_no, true)}</span>
          </div>
          ${oePart.alternate_part_no ? `
          <div style="margin-bottom: 8px;">
            <span style="color: #666; font-size: 14px;">Alt. No: </span>
            <span style="color: #374151; font-size: 14px;">${oePart.alternate_part_no}</span>
          </div>` : ''}
          ${oePart.hsn_code ? `
          <div style="margin-bottom: 8px;">
            <span style="color: #666; font-size: 14px;">HSN: </span>
            <span style="color: #374151; font-size: 14px;">${oePart.hsn_code}</span>
          </div>` : ''}
          <div style="background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px; padding: 12px; margin-top: 12px;">
            ${hasDiscount ? `
              <div style="margin-bottom: 8px;">
                <span style="color: #6b7280; font-size: 14px;">MRP: </span>
                <span style="color: #9ca3af; font-size: 18px; text-decoration: line-through;">₹${oePart.mrp?.toFixed(2)}</span>
              </div>
              <div>
                <span style="color: #dc2626; font-size: 14px;">Price: </span>
                <span style="color: #dc2626; font-size: 24px; font-weight: bold;">₹${discountedPrice?.toFixed(2)}</span>
                <span style="color: #16a34a; font-size: 14px;"> (${oeDiscount}% off)</span>
              </div>
            ` : `
              <div>
                <span style="color: #6b7280; font-size: 14px;">MRP: </span>
                <span style="color: #dc2626; font-size: 24px; font-weight: bold;">₹${oePart.mrp?.toFixed(2)}</span>
              </div>
            `}
          </div>
          ${mrpChangeHtml}
          <div style="margin-top: 16px; display: flex; align-items: center; justify-content: center; gap: 8px; padding-top: 12px; border-top: 1px solid #e5e7eb;">
            <img src="${logoUrl}" style="height: 28px; width: auto;" onerror="this.style.display='none'" />
            <span style="color: #dc2626; font-weight: bold; font-size: 14px;">Jai Paras Spares</span>
          </div>
        </div>
      `;

      document.body.appendChild(shareCard);

      // Wait for image to load if we have a product image
      if (hasProductImage) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      // Capture the card as image
      const canvas = await html2canvas(shareCard, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false
      });

      // Cleanup
      if (shareCard && shareCard.parentNode) {
        document.body.removeChild(shareCard);
      }
      shareCard = null;

      // Convert to blob
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((b) => {
          if (b) resolve(b);
          else reject(new Error('Failed to create blob'));
        }, 'image/png', 0.9);
      });
      const file = new File([blob], `OE_${oePart.oe_part_no}.png`, { type: 'image/png' });

      // Share with native API if available
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            title: `${oePart.part_name} - Eicher OE Part`,
            text: `Eicher OE Part\n${oePart.part_name}\n${formatOeDisplay(oePart.oe_part_no)}\nMRP: ₹${oePart.mrp?.toLocaleString()}${hasDiscount ? `\nYour Price: ₹${discountedPrice?.toLocaleString()}` : ''}${mrpChangeText}\n\nJai Paras Spares`,
            files: [file],
          });
          toast.success('Shared successfully!');
          return;
        } catch (e) {
          if (e.name === 'AbortError') {
            return;
          }
          console.error('Share failed:', e);
        }
      }
      
      // Fallback: Download image
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `OE_${oePart.oe_part_no}.png`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      
      toast.success('Image downloaded!');
    } catch (error) {
      console.error('Share error:', error);
      toast.error('Failed to share');
    } finally {
      if (shareCard && shareCard.parentNode) {
        document.body.removeChild(shareCard);
      }
      setSharingOe(null);
    }
  };

  // Share comparison card (OE + JP part) via WhatsApp (Admin only) - with side-by-side image
  const shareComparisonCard = async (oePart, jpPart) => {
    if (!isAdmin || !oePart || !jpPart) return;
    
    setSharingOe(`compare-${oePart.oe_part_no}`);
    
    try {
      // First, fetch the JP part image using the correct API endpoint
      let jpImageDataUrl = null;
      if (jpPart.image) {
        try {
          const imgResponse = await axios.get(`${API}/image/${encodeURIComponent(jpPart.image)}`);
          if (imgResponse.data.data_url) {
            jpImageDataUrl = imgResponse.data.data_url;
          }
        } catch (e) {
          console.log('Could not fetch JP image:', e);
        }
      }

      // Create a temporary card element for html2canvas
      const shareCard = document.createElement('div');
      shareCard.id = 'comparison-share-card';
      shareCard.style.cssText = 'position: absolute; left: -9999px; top: 0; width: 700px; font-family: Arial, Helvetica, sans-serif;';

      const logoUrl = window.location.origin + '/jp_logo.png';
      
      // No image placeholder as data URL (simple package icon)
      const noImageSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M16.5 9.4l-9-5.19M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>`;
      const noImageDataUrl = `data:image/svg+xml,${encodeURIComponent(noImageSvg)}`;
      
      // Calculate discount info for OE
      const hasOeDiscount = oeDiscount > 0 && oePart.discounted_price;
      const oeDiscountedPrice = oePart.discounted_price || oePart.mrp;
      
      // Calculate discount info for JP
      const hasJpDiscount = jpPart.discounted_price && jpPart.discounted_price < jpPart.mrp;
      const jpDiscountPercent = hasJpDiscount ? Math.round((1 - jpPart.discounted_price / jpPart.mrp) * 100) : 0;

      // MRP change calculation for comparison card
      const oldMrpComp = oePart.old_mrp;
      const newMrpComp = oePart.mrp;
      const effDateComp = oePart.mrp_effective_date;
      let mrpChangeHtmlComp = '';
      let mrpChangeTextComp = '';
      if (oldMrpComp && effDateComp && oldMrpComp !== newMrpComp) {
        const effMs = new Date(effDateComp).getTime();
        const daysSince = (Date.now() - effMs) / (1000 * 60 * 60 * 24);
        if (daysSince <= 60) {
          const change = newMrpComp - oldMrpComp;
          const pct = ((change / oldMrpComp) * 100).toFixed(1);
          const isUp = change > 0;
          mrpChangeHtmlComp = `
            <div style="background: ${isUp ? '#fef2f2' : '#f0fdf4'}; border: 1px solid ${isUp ? '#fecaca' : '#bbf7d0'}; border-radius: 6px; padding: 6px 8px; margin-top: 8px;">
              <div style="font-size: 11px; font-weight: bold; color: ${isUp ? '#dc2626' : '#16a34a'};">
                OE MRP ${isUp ? '↑' : '↓'} ${isUp ? '+' : ''}${pct}% (was ₹${oldMrpComp?.toLocaleString()})
              </div>
            </div>
          `;
          mrpChangeTextComp = `\nOE MRP Change: ₹${oldMrpComp?.toLocaleString()} → ₹${newMrpComp?.toLocaleString()} (${isUp ? '+' : ''}${pct}%)`;
        }
      }

      // IMAGE LOGIC: If JP image exists, use it for both cards. NO placeholder icon - only show real product image.
      // If no JP image available, we skip rendering the image entirely or show empty space
      const hasProductImage = !!jpImageDataUrl;
      const productImageUrl = jpImageDataUrl; // Only use real image, no placeholder fallback

      // Build the HTML using a table-based layout for reliable rendering in html2canvas
      shareCard.innerHTML = `
        <table cellpadding="0" cellspacing="0" border="0" width="700" style="border-collapse: collapse; background-color: #f3f4f6; border-radius: 12px; overflow: hidden;">
          <!-- HEADER ROW -->
          <tr>
            <td colspan="2" style="background-color: #1e3a5f; padding: 20px 0; text-align: center;">
              <!-- Title - font-weight: 700 for bold -->
              <div style="color: #ffffff; font-size: 32px; font-weight: 700; margin: 0 0 18px 0; padding: 0; letter-spacing: 1px; text-align: center;">⚡ Price Comparison</div>
              <!-- Badges Row - Simple text, no backgrounds -->
              <table cellpadding="0" cellspacing="0" border="0" width="100%">
                <tr>
                  <td width="45%" style="text-align: center; vertical-align: middle;">
                    <span style="color: #f59e0b; font-size: 20px; font-weight: 700; line-height: 1;">EICHER OE</span>
                  </td>
                  <td width="10%" style="text-align: center; vertical-align: middle;">
                    <span style="color: #ffffff; font-size: 26px; font-weight: 700; line-height: 1;">VS</span>
                  </td>
                  <td width="45%" style="text-align: center; vertical-align: middle;">
                    <span style="color: #dc2626; font-size: 20px; font-weight: 700; line-height: 1;">JP PART</span>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          
          <!-- PRODUCT CARDS ROW -->
          <tr>
            <td style="padding: 20px 10px 20px 20px; vertical-align: top; width: 50%;">
              <!-- OE Card -->
              <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color: #fef3c7; border-radius: 12px; overflow: hidden;">
                <!-- Image Section - NO padding/margin, image fills container edge-to-edge -->
                <tr>
                  <td style="background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); height: 180px; padding: 0; margin: 0; text-align: center; vertical-align: middle;">
                    ${hasProductImage ? `<img src="${productImageUrl}" style="width: 100%; height: 100%; object-fit: contain; display: block; margin: 0; padding: 0;" />` : `<div style="width: 100%; height: 100%;"></div>`}
                  </td>
                </tr>
                <!-- Content Section -->
                <tr>
                  <td style="background-color: #ffffff; padding: 18px;">
                    <div style="font-size: 15px; font-weight: 700; color: #1f2937; margin-bottom: 6px; text-transform: uppercase; line-height: 1.3;">${oePart.part_name}</div>
                    <div style="font-size: 13px; color: #6b7280; margin-bottom: 14px;">${formatOeDisplay(oePart.oe_part_no, true)}</div>
                    <div style="height: 1px; background-color: #e5e7eb; margin: 12px 0;"></div>
                    <div style="font-size: 14px; color: #6b7280; margin-bottom: 8px;">MRP: <span style="text-decoration: line-through; color: #9ca3af;">₹${oePart.mrp?.toFixed(2)}</span></div>
                    <!-- Price & Badge Row - display:flex, align-items:center for perfect vertical alignment -->
                    <div style="display: flex; flex-direction: row; align-items: center; gap: 10px; margin: 0; padding: 0;">
                      <span style="font-size: 14px; color: #374151; margin: 0; padding: 0; line-height: 1;">Price:</span>
                      <span style="font-size: 22px; font-weight: 700; color: #dc2626; margin: 0; padding: 0; line-height: 1;">₹${hasOeDiscount ? oeDiscountedPrice?.toFixed(2) : oePart.mrp?.toFixed(2)}</span>
                      ${hasOeDiscount ? `<span style="color: #22c55e; font-size: 14px; font-weight: 700; margin: 0; padding: 0; line-height: 1;">(${oeDiscount}% off)</span>` : ''}
                    </div>
                    ${mrpChangeHtmlComp}
                  </td>
                </tr>
              </table>
            </td>
            
            <td style="padding: 20px 20px 20px 10px; vertical-align: top; width: 50%;">
              <!-- JP Card -->
              <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color: #dcfce7; border-radius: 12px; overflow: hidden;">
                <!-- Image Section - NO padding/margin, image fills container edge-to-edge -->
                <tr>
                  <td style="background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%); height: 180px; padding: 0; margin: 0; text-align: center; vertical-align: middle;">
                    ${hasProductImage ? `<img src="${productImageUrl}" style="width: 100%; height: 100%; object-fit: contain; display: block; margin: 0; padding: 0;" />` : `<div style="width: 100%; height: 100%;"></div>`}
                  </td>
                </tr>
                <!-- Content Section -->
                <tr>
                  <td style="background-color: #ffffff; padding: 18px;">
                    <div style="font-size: 15px; font-weight: 700; color: #1f2937; margin-bottom: 6px; text-transform: uppercase; line-height: 1.3;">${jpPart.part_name}</div>
                    <div style="font-size: 13px; color: #6b7280; margin-bottom: 14px;">Part No: <span style="color: #dc2626; font-weight: 600;">${jpPart.new_jp_part_no || jpPart.jp_part_no}</span></div>
                    <div style="height: 1px; background-color: #e5e7eb; margin: 12px 0;"></div>
                    <div style="font-size: 14px; color: #6b7280; margin-bottom: 8px;">MRP: <span style="text-decoration: line-through; color: #9ca3af;">₹${jpPart.mrp?.toFixed(2)}</span></div>
                    <!-- Price & Badge Row - display:flex, align-items:center for perfect vertical alignment -->
                    <div style="display: flex; flex-direction: row; align-items: center; gap: 10px; margin: 0; padding: 0;">
                      <span style="font-size: 14px; color: #374151; margin: 0; padding: 0; line-height: 1;">Price:</span>
                      <span style="font-size: 22px; font-weight: 700; color: #dc2626; margin: 0; padding: 0; line-height: 1;">₹${hasJpDiscount ? jpPart.discounted_price?.toFixed(2) : jpPart.mrp?.toFixed(2)}</span>
                      ${hasJpDiscount ? `<span style="color: #22c55e; font-size: 14px; font-weight: 700; margin: 0; padding: 0; line-height: 1;">(${jpDiscountPercent}% off)</span>` : ''}
                    </div>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          
          <!-- FOOTER ROW -->
          <tr>
            <td colspan="2" style="background-color: #ffffff; padding: 14px; text-align: center; border-top: 1px solid #e5e7eb;">
              <img src="${logoUrl}" style="height: 32px; width: auto; vertical-align: middle; margin-right: 10px;" onerror="this.style.display='none'" />
              <span style="color: #dc2626; font-weight: 700; font-size: 16px; vertical-align: middle;">Jai Paras Spares</span>
            </td>
          </tr>
        </table>
      `;

      document.body.appendChild(shareCard);

      // Wait a bit for images to load
      await new Promise(resolve => setTimeout(resolve, 300));

      // Capture the card as image
      const canvas = await html2canvas(shareCard, {
        backgroundColor: '#f3f4f6',
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
      });

      document.body.removeChild(shareCard);

      // Convert to blob
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png', 0.9));
      const file = new File([blob], `Compare_${oePart.oe_part_no}_vs_${jpPart.new_jp_part_no || jpPart.jp_part_no}.png`, { type: 'image/png' });

      // Generate product link
      const productLink = `${window.location.origin}/catalog?search=${encodeURIComponent(jpPart.jp_part_no)}`;

      // Share with native API if available
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            title: `OE vs JP Part Comparison`,
            text: `Part Comparison\n\nEicher ${formatOeDisplay(oePart.oe_part_no)}\nMRP: ₹${oePart.mrp?.toLocaleString()}${hasOeDiscount ? `\nYour Price: ₹${oeDiscountedPrice?.toLocaleString()}` : ''}${mrpChangeTextComp}\n\nJP Part: ${jpPart.new_jp_part_no || jpPart.jp_part_no}\nMRP: ₹${jpPart.mrp?.toLocaleString()}${hasJpDiscount ? `\nYour Price: ₹${jpPart.discounted_price?.toLocaleString()}` : ''}\n\nOrder JP Part: ${productLink}\n\nJai Paras Spares`,
            files: [file],
          });
          toast.success('Shared successfully!');
          return;
        } catch (e) {
          if (e.name === 'AbortError') {
            return;
          }
          console.error('Share failed:', e);
        }
      }
      
      // Fallback: Download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Compare_${oePart.oe_part_no}_vs_${jpPart.new_jp_part_no || jpPart.jp_part_no}.png`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      
      toast.success('Image downloaded!');
    } catch (error) {
      console.error('Share error:', error);
      toast.error('Failed to share');
    } finally {
      setSharingOe(null);
    }
  };

  // Search parts by name, part number, OE number - with pagination support
  const searchParts = async (query) => {
    if (!token) {
      // If not logged in, navigate to catalog with search
      navigate(`/catalog?search=${encodeURIComponent(query)}`);
      return;
    }
    
    // CRITICAL: Cancel any previous search request
    if (jpSearchAbortRef.current) {
      jpSearchAbortRef.current.abort();
    }
    
    // Create new abort controller for this request
    const abortController = new AbortController();
    jpSearchAbortRef.current = abortController;
    
    // Store the current query and increment request ID
    const thisQuery = query.trim();
    const thisRequestId = ++jpRequestIdRef.current;
    currentJpQueryRef.current = thisQuery;
    
    setIsSearching(true);
    try {
      // Get selected brand from localStorage (set by CategorySelection page)
      const selectedBrand = localStorage.getItem('selectedCategory') || '';
      const brandParam = selectedBrand ? `&brand=${encodeURIComponent(selectedBrand)}` : '';
      
      const response = await axios.get(
        `${API}/categories/All%20Products/parts?search=${encodeURIComponent(thisQuery)}&limit=50${brandParam}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          signal: abortController.signal
        }
      );
      
      // CRITICAL: Triple check - request ID, query match, and abort state
      // This bulletproofs against race conditions where slow responses arrive after fast ones
      if (thisRequestId !== jpRequestIdRef.current) {
        console.log(`[JP Search] Discarding stale results (request #${thisRequestId}, current #${jpRequestIdRef.current})`);
        return;
      }
      if (currentJpQueryRef.current !== thisQuery) {
        console.log(`[JP Search] Query changed, discarding results for "${thisQuery}"`);
        return;
      }
      
      const allParts = response.data.parts || [];
      const total = response.data.total || allParts.length;
      
      // Store all results but only display first batch
      setSearchResults(allParts);
      setTotalResults(total);
      
      // Display initial batch (12 items)
      const initialBatch = allParts.slice(0, RESULTS_PER_PAGE);
      setDisplayedResults(initialBatch);
      setHasMore(allParts.length > RESULTS_PER_PAGE);
    } catch (error) {
      // Don't treat abort as an error
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        console.log(`[JP Search] Request aborted for "${thisQuery}"`);
        return;
      }
      // Only update error state if this is still current request
      if (thisRequestId === jpRequestIdRef.current) {
        console.error('Error searching parts:', error);
        setSearchResults([]);
        setDisplayedResults([]);
        setTotalResults(0);
        setHasMore(false);
      }
    } finally {
      // Only clear loading if this is still the current request
      if (thisRequestId === jpRequestIdRef.current) {
        setIsSearching(false);
      }
    }
  };

  // Load more results (for infinite scroll)
  const loadMoreResults = () => {
    const currentLength = displayedResults.length;
    const nextBatch = searchResults.slice(currentLength, currentLength + RESULTS_PER_PAGE);
    setDisplayedResults(prev => [...prev, ...nextBatch]);
    setHasMore(currentLength + nextBatch.length < searchResults.length);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      if (!token) {
        navigate(`/catalog?search=${encodeURIComponent(searchQuery)}`);
      }
      // If logged in, the live search is already showing results
      setShowJpSuggestions(false);
    } else {
      navigate(`/catalog${
        (selectedMasterCategory && selectedMasterCategory !== 'All Products')
          ? `?brand=${encodeURIComponent(selectedMasterCategory)}`
          : ''
      }`);
    }
  };

  const clearSearch = () => {
    // Log zero-click if there were results
    if (currentSearchQuery && displayedResults.length > 0) {
      logZeroClickSearch(currentSearchQuery, displayedResults.length);
    }
    setSearchQuery('');
    setSearchResults([]);
    setDisplayedResults([]);
    setTotalResults(0);
    setHasMore(false);
    setCurrentSearchQuery('');
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion) => {
    setSearchQuery(suggestion);
    setShowJpSuggestions(false);
  };

  // Handle part card click - log for personalization
  const handlePartClick = (part) => {
    logSearchClick(part, currentSearchQuery || searchQuery);
  };

  const nextBanner = () => setCurrentBanner((prev) => (prev + 1) % banners.length);
  const prevBanner = () => setCurrentBanner((prev) => (prev - 1 + banners.length) % banners.length);

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: '#0B1220' }}>
      {/* Hero Section — Variant 1 "Stage Spotlight" — premium dark navy
          glass treatment matching the BrandLandingPage tone. The actual
          search wiring, refs, debounce, and OE lookup are preserved 1:1
          from the original implementation; only the visual chrome changed. */}
      <section
        className="relative isolate overflow-hidden"
        data-testid="hero-section"
      >
        {/* Wholesale-grade ambient depth — replaces the grid backdrop.
            Two soft warm orbs in the corners + a cool rim wash give the
            page weight without the harsh line pattern. */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(155deg, #0F1320 0%, #0B1220 45%, #07101C 100%)',
          }}
        />
        <div
          aria-hidden
          className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full pointer-events-none opacity-70"
          style={{
            background:
              'radial-gradient(circle, rgba(244,162,97,0.18) 0%, rgba(255,138,90,0.08) 45%, transparent 75%)',
            filter: 'blur(72px)',
          }}
        />
        <div
          aria-hidden
          className="absolute -bottom-40 -right-24 w-[640px] h-[640px] rounded-full pointer-events-none opacity-50"
          style={{
            background:
              'radial-gradient(circle, rgba(56,114,224,0.18) 0%, rgba(56,114,224,0.07) 45%, transparent 78%)',
            filter: 'blur(90px)',
          }}
        />
        {/* Hero ambient glow disc (entry animation) */}
        <div
          aria-hidden
          className="absolute -top-20 left-1/2 -translate-x-1/2 w-[800px] h-[600px] rounded-full pointer-events-none opacity-0 animate-[heroGlow_1.6s_ease_.3s_forwards]"
          style={{
            background:
              'radial-gradient(circle, rgba(244,162,97,0.10) 0%, rgba(255,90,95,0.05) 38%, transparent 72%)',
            filter: 'blur(80px)',
          }}
        />
        <div className="container mx-auto px-4 md:px-8 lg:px-12 py-14 md:py-20 relative">
          <div className="max-w-3xl">
            {/* Brands switcher — "All Brands" toggle reveals a dropdown
                with every master category. Hover-to-open on desktop;
                tap toggles open on touch via the click handler. */}
            <div
              className="relative inline-block mb-6 group"
              data-testid="brands-switcher"
              onMouseLeave={() => setBrandsMenuOpen(false)}
            >
              <button
                type="button"
                onClick={() => setBrandsMenuOpen((v) => !v)}
                onMouseEnter={() => setBrandsMenuOpen(true)}
                className="-ml-2 inline-flex items-center gap-2 text-white/75 hover:text-white hover:bg-white/[0.08] rounded-full border border-white/10 backdrop-blur-sm h-9 px-3 transition-colors"
                data-testid="back-to-brands-btn"
                aria-haspopup="true"
                aria-expanded={brandsMenuOpen}
              >
                <Home className="w-4 h-4" />
                <span className="text-sm">All Brands</span>
                <ChevronDown
                  className={`w-3.5 h-3.5 opacity-60 transition-transform duration-200 ${brandsMenuOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {/* Dropdown panel */}
              <div
                className={`absolute left-0 top-full mt-2 w-60 rounded-xl bg-[#10182A]/95 backdrop-blur-xl border border-white/10 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.6)] z-30 overflow-hidden transition-all duration-200 origin-top ${brandsMenuOpen ? 'opacity-100 scale-100 pointer-events-auto' : 'opacity-0 scale-95 pointer-events-none'}`}
                role="menu"
              >
                <button
                  type="button"
                  onClick={() => { setBrandsMenuOpen(false); navigate('/'); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-white/75 hover:bg-white/[0.06] hover:text-white text-sm transition-colors border-b border-white/5"
                  data-testid="brands-switcher-all"
                >
                  <Home className="w-3.5 h-3.5 opacity-70" />
                  All Brands
                </button>
                {[
                  { name: 'Eicher',        dot: '#F4A261' },
                  { name: 'Swaraj Mazda',  dot: '#10B981' },
                  { name: 'Mahindra',      dot: '#EF4444' },
                  { name: 'Tata',          dot: '#3B82F6' },
                ].map((b) => {
                  const active = (selectedMasterCategory || '').toLowerCase() === b.name.toLowerCase();
                  return (
                    <button
                      key={b.name}
                      type="button"
                      onClick={() => {
                        setBrandsMenuOpen(false);
                        navigate(`/search/${encodeURIComponent(b.name)}`);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${active ? 'bg-white/[0.06] text-white' : 'text-white/75 hover:bg-white/[0.06] hover:text-white'}`}
                      data-testid={`brands-switcher-${b.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: b.dot }} />
                      <span className="flex-1 text-left">For {b.name}</span>
                      {active && <span className="text-[10px] uppercase tracking-wider text-white/40">Current</span>}
                    </button>
                  );
                })}
              </div>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-heading font-extrabold tracking-tight leading-[1.04] mb-5 md:mb-6 opacity-0 animate-[fadeUp_.8s_ease_.15s_forwards]">
              India&apos;s Premium{' '}
              <span className="bg-gradient-to-r from-[#FF5A5F] via-[#FF8A5A] to-[#FFC75F] bg-clip-text text-transparent">
                Wholesale Hub for {selectedMasterCategory || 'Eicher'}-Compatible Spares.
              </span>
            </h1>
            <p className="text-base md:text-lg text-white/70 mb-6 md:mb-8 max-w-2xl opacity-0 animate-[fadeUp_.9s_ease_.25s_forwards] leading-relaxed">
              Direct access to an exhaustive inventory of high-grade,
              OEM-spec components. Dispatched same-day from our Delhi hubs to
              power your retail network nationwide.
            </p>

            {/* Trust ribbon */}
            <div className="mb-7 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm opacity-0 animate-[fadeUp_1s_ease_.35s_forwards]">
              <span className="inline-flex items-center gap-2 text-white/75 rounded-full bg-white/[0.04] border border-white/10 px-3.5 py-1.5 backdrop-blur-sm">
                <ShieldCheck className="w-4 h-4 text-emerald-400" />
                Quality Inspected
              </span>
              <span className="inline-flex items-center gap-2 text-white/75 rounded-full bg-white/[0.04] border border-white/10 px-3.5 py-1.5 backdrop-blur-sm">
                <Wrench className="w-4 h-4 text-amber-400" />
                OE-Spec Fitment
              </span>
              <span className="inline-flex items-center gap-2 text-white/75 rounded-full bg-white/[0.04] border border-white/10 px-3.5 py-1.5 backdrop-blur-sm">
                <Truck className="w-4 h-4 text-sky-400" />
                Pan-India Despatch
              </span>
            </div>

            {/* Search Bar with Suggestions — glass plate */}
            <form onSubmit={handleSearch} className="mb-4 opacity-0 animate-[fadeUp_1.05s_ease_.45s_forwards]">
              <div className="rounded-2xl bg-white/[0.04] border border-white/10 backdrop-blur-md shadow-[0_8px_32px_rgba(0,0,0,0.4)] p-2 md:p-3 max-w-3xl">
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 z-10" />
                    <Input
                      ref={jpSearchInputRef}
                      type="text"
                      placeholder="Search by part number, name, or OE code…"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onFocus={() => { setShowJpSuggestions(true); }}
                      className="pl-12 pr-10 h-12 rounded-xl bg-black/30 border border-white/10 text-white placeholder:text-white/40 focus-visible:ring-1 focus-visible:ring-[#FFC75F]/50 focus-visible:border-[#FFC75F]/50 w-full"
                      data-testid="hero-search-input"
                      autoComplete="off"
                    />
                    {/* Search Suggestions Dropdown */}
                    {token && (
                      <SearchSuggestions
                        query={searchQuery}
                        onSelect={handleSuggestionSelect}
                        isVisible={showJpSuggestions && !searchQuery.trim()}
                        onClose={() => setShowJpSuggestions(false)}
                        inputRef={jpSearchInputRef}
                      />
                    )}
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={clearSearch}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                  <Button
                    type="submit"
                    className="h-12 px-6 bg-white text-slate-900 hover:bg-slate-100 rounded-xl uppercase tracking-wider font-bold text-sm shadow-[0_0_24px_rgba(255,255,255,0.12)] hover:shadow-[0_0_32px_rgba(255,255,255,0.22)] transition-shadow"
                    data-testid="hero-search-button"
                  >
                    Search
                  </Button>
                </div>

                {/* OE MRP Search Bar - Shown for Eicher AND Swaraj Mazda. The
                    backend filters the OE catalog by brand so each OEM stays
                    isolated (Mazda numbers can never bleed into Eicher results). */}
                {token && (selectedMasterCategory === 'Eicher' || selectedMasterCategory === 'Swaraj Mazda') && (
                  <div className="mt-3 pt-3 border-t border-white/10 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <span className="text-[10px] font-bold tracking-[0.18em] uppercase text-amber-400 shrink-0 pl-1">
                      {selectedMasterCategory === 'Swaraj Mazda' ? 'Mazda OE MRP Lookup' : 'OE MRP Lookup'}
                    </span>
                    <div className="relative flex-1">
                      <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-400/80" />
                      <Input
                        type="text"
                        placeholder={selectedMasterCategory === 'Swaraj Mazda'
                          ? 'Enter Mazda OE part number (e.g. S8980043191)…'
                          : 'Enter OE part number (e.g. IB004428)…'}
                        value={oeSearchQuery}
                        onChange={(e) => setOeSearchQuery(e.target.value)}
                        onFocus={(e) => e.target.select()}
                        className="pl-9 pr-9 h-10 rounded-lg bg-amber-500/[0.06] border border-amber-400/20 text-white placeholder:text-white/40 focus-visible:ring-1 focus-visible:ring-amber-400/40 focus-visible:border-amber-400/40 text-sm w-full"
                        data-testid="oe-search-input"
                      />
                      {oeSearchQuery && (
                        <button
                          type="button"
                          onClick={clearOeSearch}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <Button
                      type="button"
                      onClick={() => oeSearchQuery.trim() && searchOeParts(oeSearchQuery.trim())}
                      className="h-10 px-4 bg-amber-400/20 hover:bg-amber-400/30 text-amber-200 hover:text-white border border-amber-400/30 rounded-lg uppercase tracking-wider font-bold text-xs"
                      data-testid="oe-search-button"
                    >
                      Lookup
                    </Button>
                  </div>
                )}
              </div>
            </form>

            {token && searchQuery.trim() && (
              <p className="text-xs text-white/50 mb-4 inline-flex items-center gap-1.5">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Live search — results appear as you type
              </p>
            )}

            {!searchQuery.trim() && !oeSearchQuery.trim() && (
              <Button
                onClick={() => navigate(`/catalog${
                  (selectedMasterCategory && selectedMasterCategory !== 'All Products')
                    ? `?brand=${encodeURIComponent(selectedMasterCategory)}`
                    : ''
                }`)}
                size="lg"
                className="mt-2 h-12 px-7 bg-transparent border border-white/15 hover:bg-white/[0.06] hover:border-white/30 text-white rounded-xl uppercase tracking-wider font-semibold text-sm transition-all opacity-0 animate-[fadeUp_1.15s_ease_.55s_forwards]"
                data-testid="browse-catalog-button"
              >
                Browse Full Catalog
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            )}
          </div>
        </div>

        {/* Local keyframes — same as BrandLandingPage so animations are
            consistent across the home → search-landing journey. */}
        <style>{`
          @keyframes fadeUp {
            0% { opacity: 0; transform: translateY(18px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          @keyframes heroGlow {
            0%   { opacity: 0; transform: scale(0.7); }
            100% { opacity: 1; transform: scale(1); }
          }
          @media (prefers-reduced-motion: reduce) {
            [data-testid="hero-section"] * { animation: none !important; opacity: 1 !important; }
          }
        `}</style>
      </section>

      {/* Search Results Section - Shows when user is searching */}
      {token && searchQuery.trim() && (
        <section ref={jpSearchResultsRef} className="py-10" data-testid="search-results-section">
          <div className="container mx-auto px-4 md:px-8 lg:px-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-white tracking-tight">
                Search Results
              </h2>
              <Button
                variant="outline"
                onClick={clearSearch}
                className="text-white/80 border-white/15 bg-white/[0.04] hover:bg-white/[0.08] hover:text-white"
              >
                Clear Search
              </Button>
            </div>

            {isSearching ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-[#FFC75F]" />
                <span className="ml-3 text-white/60">Searching…</span>
              </div>
            ) : displayedResults.length > 0 ? (
              <>
                <p className="text-sm text-white/50 mb-4">
                  Showing {displayedResults.length} of {totalResults || searchResults.length} products matching &quot;{searchQuery}&quot;
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
                  {displayedResults.map((part) => (
                    <div key={`${part.jp_part_no}-${part.image || 'no-img'}`} onClick={() => handlePartClick(part)}>
                      <PartCard part={part} onProductUpdated={() => searchParts(searchQuery)} />
                    </div>
                  ))}
                </div>
                
                {/* Load More Button */}
                {hasMore && (
                  <div className="flex justify-center mt-8">
                    <Button
                      onClick={loadMoreResults}
                      variant="outline"
                      className="px-8 py-3 border-white/15 bg-white/[0.04] text-white hover:bg-white/[0.08]"
                    >
                      Load More Results ({searchResults.length - displayedResults.length} remaining)
                    </Button>
                  </div>
                )}
              </>
            ) : searchQuery.trim() ? (
              <div className="text-center py-20">
                <Package className="w-16 h-16 mx-auto text-white/20 mb-4" />
                <p className="text-white/70">No products found matching &quot;{searchQuery}&quot;</p>
                <p className="text-sm text-white/40 mt-2">
                  Try different keywords or check your spelling
                </p>
              </div>
            ) : null}
          </div>
        </section>
      )}

      {/* OE MRP Search Results Section */}
      {token && oeSearchQuery.trim() && (
        <section ref={oeSearchResultsRef} className="py-6 md:py-10 border-t border-white/5" data-testid="oe-search-results-section">
          <div className="container mx-auto px-4 md:px-8 lg:px-12">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-white flex items-center gap-2 tracking-tight">
                  <Package className="w-5 h-5 md:w-6 md:h-6 text-amber-400" />
                  OE MRP Results
                </h2>
                {oeDiscount > 0 && (
                  <p className="text-sm text-amber-300/80 mt-1">
                    Your OE Discount: {oeDiscount}% applied
                  </p>
                )}
              </div>
              <Button
                variant="outline"
                onClick={clearOeSearch}
                className="text-white/80 border-amber-400/30 bg-amber-400/[0.06] hover:bg-amber-400/15 hover:text-white w-full sm:w-auto"
              >
                Clear OE Search
              </Button>
            </div>

            {isOeSearching ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
                <span className="ml-3 text-white/60">Searching OE parts…</span>
              </div>
            ) : oeSearchResults.length > 0 ? (
              <>
                <p className="text-sm text-white/50 mb-4">
                  Found {oeSearchResults.length} OE parts matching &quot;{oeSearchQuery}&quot;
                </p>
                <div className="space-y-6">
                  {oeSearchResults.map((result, idx) => (
                    <div key={idx} className="flex flex-col md:flex-row gap-4 items-stretch md:items-start">
                      {/* OE Part Card - Compact text-only (only show if there's an OE part from the database) */}
                      {result.oe_part && (
                        <div className="w-full md:w-64 flex-shrink-0 bg-white rounded-lg border-2 border-amber-400 shadow-md p-4 relative">
                          {/* Admin Share Buttons */}
                          {isAdmin && (
                            <div className="absolute top-2 right-2 flex gap-1">
                              <button
                                onClick={() => shareOePart(result.oe_part, result.jp_parts?.[0] || null)}
                                disabled={sharingOe === result.oe_part?.oe_part_no}
                                className="bg-green-500 hover:bg-green-600 text-white p-1.5 rounded-full shadow-sm transition-all"
                                title="Share OE Part via WhatsApp"
                              >
                                <Share2 className={`w-3.5 h-3.5 ${sharingOe === result.oe_part?.oe_part_no ? 'animate-pulse' : ''}`} />
                              </button>
                              {result.jp_parts && result.jp_parts.length > 0 && (
                                <button
                                  onClick={() => shareComparisonCard(result.oe_part, result.jp_parts[0])}
                                  disabled={sharingOe === `compare-${result.oe_part?.oe_part_no}`}
                                  className="bg-blue-500 hover:bg-blue-600 text-white p-1.5 rounded-full shadow-sm transition-all"
                                  title="Share OE vs JP Comparison"
                                >
                                  <span className={`text-xs font-bold ${sharingOe === `compare-${result.oe_part?.oe_part_no}` ? 'animate-pulse' : ''}`}>VS</span>
                                </button>
                              )}
                            </div>
                          )}
                          <div className="flex items-center gap-2 mb-3">
                            <span className="bg-amber-500 text-white text-xs font-bold px-2 py-1 rounded">OE MRP</span>
                          </div>
                          <h3 className="font-bold text-gray-900 text-sm mb-3 line-clamp-2">
                            {result.oe_part?.part_name}
                          </h3>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-500">OE Part No:</span>
                              <span className="font-mono font-bold text-gray-900">{formatOeDisplay(result.oe_part?.oe_part_no, true)}</span>
                            </div>
                            {result.oe_part?.alternate_part_no && (
                              <div className="flex justify-between">
                                <span className="text-gray-500">Alt. No:</span>
                                <span className="font-mono text-gray-700 text-xs">{result.oe_part?.alternate_part_no}</span>
                              </div>
                            )}
                            <div className="flex justify-between">
                              <span className="text-gray-500">HSN:</span>
                              <span className="text-gray-700">{result.oe_part?.hsn_code || '-'}</span>
                            </div>
                            <div className="flex justify-between items-center pt-2 border-t border-gray-200 mt-2">
                              <span className="text-gray-600 font-medium">MRP:</span>
                              <span className="font-bold text-lg text-gray-900">₹{result.oe_part?.mrp?.toLocaleString()}</span>
                            </div>
                            {/* Effective-from date — always shown when present, for BOTH Eicher and
                                Swaraj Mazda OEMRP cards so buyers / staff know which price list this
                                value belongs to. The MRP-change badge below is a separate, time-bound
                                callout for very recent changes. */}
                            {result.oe_part?.mrp_effective_date && (() => {
                              const eff = new Date(result.oe_part.mrp_effective_date);
                              if (Number.isNaN(eff.getTime())) return null;
                              const label = eff.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
                              return (
                                <div className="text-[11px] text-amber-700 mt-1 font-medium flex items-center justify-end gap-1" data-testid="oe-mrp-effective-from">
                                  <span>MRP effective from {label}</span>
                                </div>
                              );
                            })()}
                            {/* MRP Change indicator - show for 60 days from effective date */}
                            {(() => {
                              const oePart = result.oe_part;
                              const oldMrp = oePart?.old_mrp;
                              const newMrp = oePart?.mrp;
                              const effDate = oePart?.mrp_effective_date;
                              if (!oldMrp || !effDate || oldMrp === newMrp) return null;
                              const effMs = new Date(effDate).getTime();
                              const nowMs = Date.now();
                              const daysSince = (nowMs - effMs) / (1000 * 60 * 60 * 24);
                              if (daysSince > 60) return null;
                              const change = newMrp - oldMrp;
                              const pct = ((change / oldMrp) * 100).toFixed(1);
                              const isUp = change > 0;
                              return (
                                <div className={`rounded-lg p-2 mt-1 ${isUp ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`} data-testid="mrp-change-badge">
                                  <div className="flex items-center gap-1 mb-1">
                                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${isUp ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                                      MRP {isUp ? 'INCREASED' : 'DECREASED'}
                                    </span>
                                  </div>
                                  <div className="flex justify-between text-xs">
                                    <span className="text-gray-500">Previous MRP:</span>
                                    <span className="text-gray-700 line-through">₹{oldMrp?.toLocaleString()}</span>
                                  </div>
                                  <div className="flex justify-between items-center text-xs mt-0.5">
                                    <span className="text-gray-500">Change:</span>
                                    <span className={`font-bold ${isUp ? 'text-red-600' : 'text-green-600'}`}>
                                      {isUp ? '↑' : '↓'} ₹{Math.abs(change).toLocaleString()} ({isUp ? '+' : ''}{pct}%)
                                    </span>
                                  </div>
                                  <div className="text-[10px] text-gray-400 mt-1">Effective: {new Date(effDate).toLocaleDateString('en-IN', {day: 'numeric', month: 'short', year: 'numeric'})}</div>
                                </div>
                              );
                            })()}
                            {oeDiscount > 0 && (
                              <div className="flex justify-between items-center bg-green-50 rounded px-2 py-1">
                                <span className="text-green-700 text-xs">Your Price:</span>
                                <span className="font-bold text-green-600">₹{result.oe_part?.discounted_price?.toLocaleString()}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* JP Part Cards - Full catalog-style cards with image and controls */}
                      {result.jp_parts && result.jp_parts.length > 0 && (
                        <div className="flex flex-col md:flex-row gap-4 flex-wrap">
                          {result.jp_parts.map((jpPart, jpIdx) => (
                            <div key={jpIdx} className="w-full md:w-72 flex-shrink-0">
                              <PartCard part={jpPart} />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-20">
                <Package className="w-16 h-16 mx-auto text-amber-300 mb-4" />
                <p className="text-gray-500">No OE parts found matching &quot;{oeSearchQuery}&quot;</p>
                <p className="text-sm text-gray-400 mt-2">
                  Try a different OE part number or alternate number
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Promotional Banners - Hidden when searching */}
      {banners.length > 0 && !searchQuery.trim() && !oeSearchQuery.trim() && (
        <section className="relative" data-testid="banner-section">
          <div className="relative h-64 md:h-80 lg:h-96 overflow-hidden">
            {banners.map((banner, idx) => (
              <div
                key={banner.banner_id}
                className={`absolute inset-0 transition-opacity duration-700 ${
                  idx === currentBanner ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
              >
                <a 
                  href={banner.link_url || '#'} 
                  onClick={(e) => !banner.link_url && e.preventDefault()}
                  className="block w-full h-full"
                >
                  <img
                    src={`${API}/image/${banner.image_url}`}
                    alt={banner.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                    <h3 className="text-white text-xl md:text-2xl font-bold">{banner.title}</h3>
                  </div>
                </a>
              </div>
            ))}
            
            {/* Navigation Arrows */}
            {banners.length > 1 && (
              <>
                <button
                  onClick={prevBanner}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={nextBanner}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
                
                {/* Dots */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {banners.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentBanner(idx)}
                      className={`w-3 h-3 rounded-full transition-colors ${
                        idx === currentBanner ? 'bg-white' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* Features Section - Hidden when searching */}
      {!searchQuery.trim() && (
        <section className="py-20 relative">
          <div className="container mx-auto px-4 md:px-8 lg:px-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              <div
                className="group relative rounded-2xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur-sm hover:border-white/25 hover:bg-white/[0.05] transition-all duration-300 overflow-hidden"
                data-testid="feature-card-parts"
              >
                <div className="absolute -bottom-0 left-0 h-[2px] w-12 bg-gradient-to-r from-[#FF5A5F] to-[#FFC75F] group-hover:w-full transition-all duration-500" />
                <div className="w-11 h-11 rounded-xl bg-rose-500/15 border border-rose-400/20 flex items-center justify-center mb-4">
                  <Package2 className="w-5 h-5 text-rose-300" />
                </div>
                <h3 className="text-lg font-heading font-bold mb-2 text-white tracking-tight">OE-Spec Parts</h3>
                <p className="text-sm text-white/60 leading-relaxed">
                  Quality-inspected catalog covering Eicher, Tata, Mahindra and Swaraj Mazda.
                </p>
              </div>

              <div
                className="group relative rounded-2xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur-sm hover:border-white/25 hover:bg-white/[0.05] transition-all duration-300 overflow-hidden"
                data-testid="feature-card-customers"
              >
                <div className="absolute -bottom-0 left-0 h-[2px] w-12 bg-emerald-400/80 group-hover:w-full transition-all duration-500" />
                <div className="w-11 h-11 rounded-xl bg-emerald-500/15 border border-emerald-400/20 flex items-center justify-center mb-4">
                  <Users className="w-5 h-5 text-emerald-300" />
                </div>
                <h3 className="text-lg font-heading font-bold mb-2 text-white tracking-tight">B2B Focused</h3>
                <p className="text-sm text-white/60 leading-relaxed">
                  Tailored for distributors and retailers with bulk ordering options.
                </p>
              </div>

              <div
                className="group relative rounded-2xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur-sm hover:border-white/25 hover:bg-white/[0.05] transition-all duration-300 overflow-hidden"
                data-testid="feature-card-delivery"
              >
                <div className="absolute -bottom-0 left-0 h-[2px] w-12 bg-sky-400/80 group-hover:w-full transition-all duration-500" />
                <div className="w-11 h-11 rounded-xl bg-sky-500/15 border border-sky-400/20 flex items-center justify-center mb-4">
                  <Truck className="w-5 h-5 text-sky-300" />
                </div>
                <h3 className="text-lg font-heading font-bold mb-2 text-white tracking-tight">Fast Processing</h3>
                <p className="text-sm text-white/60 leading-relaxed">
                  Quick order confirmation via email. We process orders promptly.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section - Hidden when searching */}
      {!searchQuery.trim() && (
        <section
          className="relative py-20 overflow-hidden border-t border-white/5"
          data-testid="cta-section"
        >
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'radial-gradient(circle at 50% 60%, rgba(255,90,95,0.10) 0%, rgba(244,162,97,0.06) 35%, transparent 70%)',
            }}
          />
          <div className="container mx-auto px-4 md:px-8 lg:px-12 text-center relative">
            <h2 className="text-3xl md:text-5xl font-heading font-extrabold mb-5 tracking-tight">
              Ready to place your{' '}
              <span className="bg-gradient-to-r from-[#FF5A5F] via-[#FF8A5A] to-[#FFC75F] bg-clip-text text-transparent">
                next order?
              </span>
            </h2>
            <p className="text-base md:text-lg text-white/65 mb-8 max-w-2xl mx-auto">
              Browse our complete catalog and place orders with ease. Get instant email confirmation.
            </p>
            <Button
              onClick={() => navigate(`/catalog${
                (selectedMasterCategory && selectedMasterCategory !== 'All Products')
                  ? `?brand=${encodeURIComponent(selectedMasterCategory)}`
                  : ''
              }`)}
              size="lg"
              className="h-12 px-8 bg-white text-slate-900 hover:bg-slate-100 rounded-xl uppercase tracking-wider font-bold text-sm shadow-[0_0_24px_rgba(255,255,255,0.12)] hover:shadow-[0_0_32px_rgba(255,255,255,0.22)] transition-shadow"
              data-testid="cta-view-catalog-button"
            >
              View Catalog Now
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </section>
      )}

      {/* Admin/Staff: Create Customer Order Button */}
      {canCreateCustomerOrder && (
        <div className="fixed bottom-6 left-6 z-50 flex flex-col gap-2">
          <Button
            onClick={() => setShowCreateOrder(true)}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded-full shadow-lg"
            data-testid="create-customer-order-button"
          >
            <ShoppingCart className="w-5 h-5 mr-2" />
            Create Order
          </Button>
          <Button
            onClick={() => setShowCatalogueOrder(true)}
            className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-3 rounded-full shadow-lg"
            data-testid="create-catalogue-order-button"
          >
            <Package className="w-5 h-5 mr-2" />
            Order via Catalogue
          </Button>
          <Button
            onClick={() => setShowCreateQuotation(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-full shadow-lg"
            data-testid="create-quotation-button"
          >
            <FileText className="w-5 h-5 mr-2" />
            Create Quotation
          </Button>
        </div>
      )}

      {/* Create Customer Order Dialog */}
      <CreateCustomerOrder 
        isOpen={showCreateOrder} 
        onClose={() => setShowCreateOrder(false)} 
      />

      {/* Catalogue Order Modal */}
      <CatalogueOrderModal 
        isOpen={showCatalogueOrder} 
        onClose={() => setShowCatalogueOrder(false)} 
      />

      {/* Create Quotation Dialog */}
      <CreateQuotationModal 
        isOpen={showCreateQuotation} 
        onClose={() => setShowCreateQuotation(false)} 
      />

      {/* Quotation Notifications - Bottom Right */}
      {token && <QuotationNotifications isAdmin={canCreateCustomerOrder} />}
      
      {/* NTO Received Notifications - Bottom Left */}
      {token && <NtoReceivedNotification token={token} />}
    </div>
  );
};

export default HomePage;
